import React from 'react';
import { motion } from 'framer-motion';
import { ScaleIcon, StarIcon } from '@heroicons/react/24/outline';
import { TeaserDemoProps } from '../../config/teaserContent';
import { createOptimizedTeaserDemo, useTeaserDemoState } from './baseTeaserDemo';

interface InteractiveEfficientFrontierDemoProps extends TeaserDemoProps {
  optimization?: {
    currentPoint: { risk: number; return: number; sharpe: number };
    optimalPoint: { risk: number; return: number; sharpe: number };
    frontierPoints: Array<{ risk: number; return: number }>;
  };
}

const InteractiveEfficientFrontierDemoComponent: React.FC<InteractiveEfficientFrontierDemoProps> = ({ 
  className, 
  aspectRatio = 'video',
  optimization = {
    currentPoint: { risk: 18.5, return: 22.3, sharpe: 1.21 },
    optimalPoint: { risk: 14.2, return: 24.8, sharpe: 1.75 },
    frontierPoints: [
      { risk: 10, return: 15 },
      { risk: 12, return: 18 },
      { risk: 14.2, return: 24.8 },
      { risk: 18, return: 28 },
      { risk: 22, return: 30 }
    ]
  }
}) => {
  const { containerClass } = useTeaserDemoState(aspectRatio);

  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
      className={`relative p-4 h-full flex flex-col justify-between ${containerClass} ${className}`}
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <motion.div
          animate={{ 
            rotate: [0, 15, -15, 0],
            scale: [1, 1.1, 1]
          }}
          transition={{ duration: 3, repeat: Infinity }}
          className="w-8 h-8 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full flex items-center justify-center shadow-lg"
        >
          <ScaleIcon className="w-4 h-4 text-white" />
        </motion.div>
        <div>
          <h4 className="text-white font-semibold text-sm">Efficient Frontier</h4>
          <p className="text-gray-300 text-xs">Risk-Return optimization</p>
        </div>
      </div>

      {/* Interactive Chart Area */}
      <div className="relative bg-black/20 rounded-lg p-3 mb-4 flex-1">
        <svg
          className="w-full h-full"
          viewBox="0 0 100 80"
          preserveAspectRatio="none"
        >
          {/* Axes */}
          <line x1="10" y1="70" x2="90" y2="70" stroke="rgba(255,255,255,0.3)" strokeWidth="1" />
          <line x1="10" y1="70" x2="10" y2="10" stroke="rgba(255,255,255,0.3)" strokeWidth="1" />
          
          {/* Axis labels */}
          <text x="50" y="78" textAnchor="middle" className="text-xs fill-gray-400">Risk →</text>
          <text x="5" y="40" textAnchor="middle" className="text-xs fill-gray-400" transform="rotate(-90, 5, 40)">Return ↑</text>

          {/* Efficient Frontier Curve */}
          <motion.path
            d="M 15 65 Q 30 50 45 25 T 85 15"
            fill="none"
            stroke="url(#frontierGradient)"
            strokeWidth="2"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 2, ease: "easeInOut" }}
          />

          {/* Gradient definition */}
          <defs>
            <linearGradient id="frontierGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#3b82f6" />
              <stop offset="50%" stopColor="#8b5cf6" />
              <stop offset="100%" stopColor="#f59e0b" />
            </linearGradient>
          </defs>

          {/* Current Portfolio Point */}
          <motion.circle
            cx="35"
            cy="45"
            r="4"
            fill="#ef4444"
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 1, duration: 0.5 }}
          />
          <motion.circle
            cx="35"
            cy="45"
            r="6"
            fill="none"
            stroke="#ef4444"
            strokeWidth="1"
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 1, 0] }}
            transition={{ delay: 1.5, duration: 2, repeat: Infinity }}
          />

          {/* Optimal Point */}
          <motion.circle
            cx="45"
            cy="25"
            r="4"
            fill="#fbbf24"
            initial={{ opacity: 0, scale: 0 }}
            animate={{ 
              opacity: 1, 
              scale: [1, 1.3, 1],
              boxShadow: [
                '0 0 10px rgba(251, 191, 36, 0.5)',
                '0 0 20px rgba(251, 191, 36, 0.8)',
                '0 0 10px rgba(251, 191, 36, 0.5)'
              ]
            }}
            transition={{ 
              scale: { delay: 2, duration: 2, repeat: Infinity },
              opacity: { delay: 2, duration: 0.5 }
            }}
          />

          {/* Optimal Point Star */}
          <motion.g
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1, rotate: 360 }}
            transition={{ delay: 2.5, duration: 1, repeat: Infinity, repeatType: "loop" }}
          >
            <StarIcon 
              x="41" 
              y="21" 
              width="8" 
              height="8" 
              className="fill-yellow-400 stroke-yellow-400" 
            />
          </motion.g>
        </svg>

        {/* Point Labels */}
        <div className="absolute top-12 left-8 text-xs">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2, duration: 0.5 }}
            className="bg-red-500/20 border border-red-500/40 rounded px-2 py-1 text-red-300"
          >
            Current: {optimization.currentPoint.sharpe}
          </motion.div>
        </div>

        <div className="absolute top-2 right-8 text-xs">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2.2, duration: 0.5 }}
            className="bg-yellow-500/20 border border-yellow-500/40 rounded px-2 py-1 text-yellow-300"
          >
            Optimal: {optimization.optimalPoint.sharpe}
          </motion.div>
        </div>
      </div>

      {/* Optimization Metrics */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 3, duration: 0.5 }}
          className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-2 text-center"
        >
          <motion.p
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="text-yellow-400 font-bold text-sm"
          >
            {optimization.optimalPoint.sharpe}
          </motion.p>
          <p className="text-xs text-gray-400">Sharpe Ratio</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 3.2, duration: 0.5 }}
          className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-2 text-center"
        >
          <motion.p
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 1.5, repeat: Infinity, delay: 0.3 }}
            className="text-green-400 font-bold text-sm"
          >
            {optimization.optimalPoint.return}%
          </motion.p>
          <p className="text-xs text-gray-400">Return</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 3.4, duration: 0.5 }}
          className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-2 text-center"
        >
          <motion.p
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 1.5, repeat: Infinity, delay: 0.6 }}
            className="text-blue-400 font-bold text-sm"
          >
            {optimization.optimalPoint.risk}%
          </motion.p>
          <p className="text-xs text-gray-400">Risk</p>
        </motion.div>
      </div>

      {/* Interactive Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 4, duration: 0.5 }}
        className="text-center"
      >
        <motion.p
          animate={{ opacity: [0.7, 1, 0.7] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="text-orange-300 text-xs font-medium"
        >
          ⚖️ Nobel Prize optimization theory
        </motion.p>
      </motion.div>
    </motion.div>
  );
};

export const InteractiveEfficientFrontierDemo = createOptimizedTeaserDemo(
  InteractiveEfficientFrontierDemoComponent,
  'InteractiveEfficientFrontierDemo'
);